/**
 * Patrón de Ruptura Quantum — wiring del indicador.
 *
 * REDISEÑO de presentación del indicador legacy `breakout_pattern` al formato
 * Quantum: la detección la sigue haciendo el motor original (intocable) y
 * este módulo solo añade el filtro opcional de activos favoritos, adapter de
 * solo lectura (adapter.js), estadísticas de dashboard (stats.js) y pintura
 * Quantum (renderer.js), todo vía createChartDrawingApi.
 */

import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import {
  SAFE_DEFAULTS,
  resolveConfig,
  symbolMatches,
} from './constants.js'
import { runAdapter } from './adapter.js'
import { computeStats } from './stats.js'
import { renderPrq } from './renderer.js'
import { getChartContext } from './context.js'

export const defaultConfig = SAFE_DEFAULTS

export { resolveConfig }

function gatedResult() {
  return { patterns: [], signals: [], activeSignal: null, lastSignal: null, stats: null, gated: true }
}

/**
 * Sanea velas de entrada: OHLC numéricos y time presente. Devuelve null si
 * la entrada no es un array (los callers devuelven entonces salida gated).
 */
function sanitizeCandles(candles) {
  if (!Array.isArray(candles)) return null
  const out = []
  for (const c of candles) {
    if (c == null || typeof c !== 'object') continue
    const open = Number(c.open)
    const high = Number(c.high)
    const low = Number(c.low)
    const close = Number(c.close)
    if (!Number.isFinite(open) || !Number.isFinite(high) || !Number.isFinite(low) || !Number.isFinite(close)) continue
    if (c.time == null || c.time === '') continue
    out.push({ time: c.time, open, high, low, close, volume: Number.isFinite(Number(c.volume)) ? Number(c.volume) : 0 })
  }
  return out
}

export function detect(candles, rawConfig) {
  try {
    const cfg = resolveConfig(rawConfig)

    // Apagado suave (patrón bollinger): el panel desactiva sin quitar el
    // indicador del registry.
    if (cfg.enabled === false) return gatedResult()

    // Gating por activo (contexto inyectado por la página). Lista vacía =
    // todos los símbolos (symbolMatches ya lo resuelve). Sin gating por
    // timeframe desde v1.2: el indicador corre siempre en el TF del chart.
    const ctx = getChartContext() || {}
    if (!symbolMatches(cfg.assets, ctx.symbol)) return gatedResult()

    const clean = sanitizeCandles(candles)
    if (!clean) return gatedResult()
    // Mínimo de barras que exige el motor: WARMUP_BARS = max(pivotLen*2 +
    // maxChannelBars, 50) y el bucle necesita n >= WARMUP_BARS + 1 (ver
    // engine.js líneas 121-125).
    const minBars = Math.max(cfg.pivotLen * 2 + cfg.maxChannelBars, 50) + 1
    if (clean.length < minBars) return gatedResult()

    const out = runAdapter(clean, cfg, ctx)
    // showLastSignals viaja en el result porque el Badge no recibe config (ni
    // DraggableRegistryBadge ni ChartPage se la pasan); es la única vía de
    // que el toggle del panel llegue a la tabla de últimas 10 señales.
    return { ...out, stats: computeStats(out.signals, cfg.budget), showLastSignals: cfg.showLastSignals }
  } catch {
    return gatedResult()
  }
}

export function render(chart, candleSeries, result, config, api) {
  renderPrq(chart, candleSeries, result, config, api)
}

export default {
  id: 'patron_ruptura_quantum',
  name: 'Patrón de Ruptura Quantum',
  version: '1.0.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
  // Anti-flicker (regla OB, mismo patrón que el resto de indicadores Quantum):
  // las primitivas de PRQ son series nativas de LWC (líneas, custom series de
  // relleno, markers) + labels HTML con su propio efecto de reposicionado —
  // TODAS panean solas. Reconstruirlas en cada frame de pan las hacía
  // aparecer/desaparecer y cambiar de forma (bug 2026-08-06): en pan puro el
  // registry NO las reconstruye; la geometría queda anclada a datos + motor.
  renderOnPan: false,
}
